import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { useAuth } from "../contexts/AuthContext";
import {
  acceptTermsAndConditions,
  fetchSystemAccessStatus,
} from "../services/systemAccessService";
import decodeJwtToken from "../auth/decodeJwtToken";
import { getPostAuthRoute } from "../shared/systemAccessRouting";

const brand = "#800b33";

export default function SystemTermsAcceptancePage() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let active = true;
    const loadStatus = async () => {
      setLoading(true);
      setError("");
      try {
        const payload = await fetchSystemAccessStatus();
        if (!active) {
          return;
        }
        setStatus(payload);
        if (payload?.next_required_action && payload.next_required_action !== "ACCEPT_TERMS") {
          navigate(getPostAuthRoute({ ...user, ...payload }), { replace: true });
        }
      } catch (err) {
        if (active) {
          setError(err.message || "Unable to load terms and conditions.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    loadStatus();
    return () => {
      active = false;
    };
  }, [navigate, user]);

  const termsContent = useMemo(
    () => status?.terms_content || "Terms and conditions are not available right now.",
    [status]
  );

  const handleAccept = async () => {
    if (!agree) {
      setError("You must confirm agreement before continuing.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const payload = await acceptTermsAndConditions();
      login(payload.access, payload.refresh);
      const decoded = decodeJwtToken(payload.access);
      Swal.fire({
        icon: "success",
        title: "Terms Accepted",
        text: "Your account has moved to the next onboarding step.",
        confirmButtonColor: brand,
      }).then(() => {
        navigate(getPostAuthRoute(decoded), { replace: true });
      });
    } catch (err) {
      setError(err.message || "Unable to accept the terms right now.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
        bgcolor: "#f7f4f7",
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 900, borderRadius: 4, boxShadow: 6 }}>
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "rgba(128, 11, 51, 0.1)",
                  color: brand,
                }}
              >
                <GavelOutlinedIcon />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: brand }}>
                  Terms and Conditions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You must accept the current terms before continuing into the platform.
                </Typography>
              </Box>
            </Stack>

            {loading ? (
              <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {error && <Alert severity="error">{error}</Alert>}
                <Alert severity="info">
                  Version required: <strong>{status?.terms_required_version || "N/A"}</strong>
                </Alert>
                <Box
                  sx={{
                    maxHeight: 360,
                    overflowY: "auto",
                    borderRadius: 3,
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    bgcolor: "#fcfcfd",
                    p: 3,
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.8,
                    color: "#1e293b",
                  }}
                >
                  {termsContent}
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox checked={agree} onChange={(event) => setAgree(event.target.checked)} />
                  }
                  label="I agree to the Terms and Conditions and platform governance policies."
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="flex-end">
                  <Button
                    variant="contained"
                    onClick={handleAccept}
                    disabled={submitting || loading}
                    sx={{ bgcolor: brand, "&:hover": { bgcolor: "#6a0a2c" } }}
                  >
                    {submitting ? "Processing..." : "Accept and Continue"}
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
